import { store } from "store";
import { CreateBase } from "easy-redux-http-call";

// ── Silent refresh state ────────────────────────────────────────────────────
// All concurrent requests that hit a 401 share the same refresh Promise so
// only one refresh call is ever in-flight at a time.
let refreshPromise = null;

async function doRefresh() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) throw new Error("no_refresh_token");

  const res = await window.fetch(`${process.env.REACT_APP_API}/refresh-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) throw new Error("refresh_failed");
  const json = await res.json();
  if (!json.status) throw new Error("refresh_rejected");

  localStorage.setItem("token", json.token);
  localStorage.setItem("refreshToken", json.refreshToken);
  return json.token;
}

function getRefreshPromise() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

function logout() {
  localStorage.clear();
  window.location.reload();
}

// ── Build fetch URL & options from easy-redux-http-call data ────────────────
function buildUrl(data) {
  if (data.method === "get") {
    const searchParams = new URLSearchParams(data.params);
    return `${data.endpoint}?${searchParams.toString()}`;
  }
  return data.endpoint;
}

function buildOptions(data, token) {
  const isMultipart = data?.headers?.["Content-Type"] === "multipart/form-data";

  if (isMultipart) {
    return {
      method: data.method,
      headers: { Authorization: token },
      body: data.params,
    };
  }

  const options = {
    method: data.method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
      ...(data.headers || {}),
    },
  };
  if (Object.keys(data.params || {}).length && data.method.toLowerCase() !== "get") {
    options.body = JSON.stringify(data.params);
  }
  return options;
}

async function executeFetch(data, token) {
  const url = buildUrl(data);
  const options = buildOptions(data, token);
  const res = await window.fetch(url, options);
  const resData = await res.json();
  return { status: res.status, data: resData };
}

// ── Main httpFn with interceptor ────────────────────────────────────────────
async function httpFn(data) {
  const token = localStorage.getItem("token");
  const result = await executeFetch(data, token);

  if (result.status !== 401) return result;

  // 401: attempt silent token refresh then retry once
  try {
    const newToken = await getRefreshPromise();
    return await executeFetch(data, newToken);
  } catch {
    logout();
    return result;
  }
}

export const apiBase = new CreateBase({
  baseApiUrl: process.env.REACT_APP_API,
  store: store,
  defaultState: { loading: false },
  validateHttpResponse: (res) => res.status,
  httpFn,
});
